import React from 'react';
import loadable from '@loadable/component';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink } from '../../components';

import css from './FooterContainer.module.css';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

/**
 * Minimal footer used when the hosted `footer` asset is absent or empty.
 *
 * The real footer is authored in Sharetribe Console and rendered by
 * PageBuilder/SectionBuilder/SectionFooter. That asset can legitimately be
 * missing (new marketplace, asset not published yet, fetch failure), in which
 * case the page previously ended on a hard edge with no closing element.
 * This fallback keeps the page terminated with the same hairline + topbar
 * surface treatment, and stays entirely token-driven.
 */
const FallbackFooter = ({ marketplaceName }) => (
  <footer className={css.fallbackRoot}>
    <div className={css.fallbackContent}>
      <span className={css.fallbackBrand}>{marketplaceName}</span>
      <div className={css.fallbackLinks}>
        <NamedLink name="AboutPage" className={css.fallbackLink}>
          About Us
        </NamedLink>
        <NamedLink name="SafetyPage" className={css.fallbackLink}>
          Safety Guide
        </NamedLink>
        <NamedLink name="TermsOfServicePage" className={css.fallbackLink}>
          Terms of Service
        </NamedLink>
        <NamedLink name="PrivacyPolicyPage" className={css.fallbackLink}>
          Privacy Policy
        </NamedLink>
      </div>
      <span className={css.fallbackCopyright}>
        <FormattedMessage
          id="FooterContainer.fallbackCopyright"
          values={{ marketplaceName, year: new Date().getFullYear() }}
        />
      </span>
    </div>
  </footer>
);

const FooterComponent = () => {
  const config = useConfiguration();
  const { footer, topbar, marketplaceName } = config || {};

  // The footer asset is hosted, so it may be missing, null, or an empty object.
  const hasFooterAsset = !!footer && typeof footer === 'object' && Object.keys(footer).length > 0;

  if (!hasFooterAsset) {
    return <FallbackFooter marketplaceName={marketplaceName || ''} />;
  }

  // The footer asset does not specify sectionId or sectionType. However, the SectionBuilder
  // expects sectionId and sectionType in order to identify the section. We add those
  // attributes here before passing the asset to SectionBuilder.
  const footerSection = {
    ...footer,
    sectionId: 'footer',
    sectionType: 'footer',
    linkLogoToExternalSite: topbar?.logoLink,
  };

  return <SectionBuilder sections={[footerSection]} />;
};

// NOTE: if you want to add dynamic data to FooterComponent,
//       you could just connect this FooterContainer to Redux Store
//
// const mapStateToProps = state => {
//   const { currentUser } = state.user;
//   return { currentUser };
// };
// const FooterContainer = compose(connect(mapStateToProps))(FooterComponent);
// export default FooterContainer;

export default FooterComponent;
